import { ref, reactive } from 'vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { updateNotificationsSidebarBadge } from '@/js/utils/notifications-sidebar-badge';
import { countManagerUnreadNotifications } from '../../resident-notifications/map-notification';
import type { NotificationRecipientsData, ReplyFormData } from '../types';
import { useToast } from 'primevue/usetoast';
import {
  fetchManagerNotificationList,
  fetchManagerNotificationRecipients,
  markManagerNotificationRead,
  type ManagerNotificationListItem,
} from '../manager-notification-api';

export function useNotificationData() {
  const messages = ref<ManagerNotificationListItem[]>([]);
  const loadingNotifications = ref(false);
  const errorNotifications = ref<string | null>(null);
  const nav = reactive({
    totalCount: 0,
    pageSize: 20,
    currentPage: 1,
    pageCount: 1,
  });
  const toast = useToast();

  const recipients = ref<NotificationRecipientsData>({
    residents: [],
    clusters: [],
  });
  const loadingResidents = ref(false);
  const errorResidents = ref<string | null>(null);

  async function loadNotifications(
    residentId: string | null = null,
    page: number = 1,
    search: string = '',
  ) {
    loadingNotifications.value = true;
    errorNotifications.value = null;

    try {
      const data = await fetchManagerNotificationList({
        q: search,
        page,
        residentId,
      });

      messages.value = data.messages;
      nav.totalCount = data.nav.totalCount;
      nav.pageSize = data.nav.pageSize;
      nav.currentPage = data.nav.currentPage;
      nav.pageCount = data.nav.pageCount;
      updateNotificationsSidebarBadge(document, countManagerUnreadNotifications(data.messages));
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
      errorNotifications.value = error instanceof Error ? error.message : 'Неизвестная ошибка';
      messages.value = [];
      nav.totalCount = 0;
      nav.pageCount = 1;
    } finally {
      loadingNotifications.value = false;
    }
  }

  async function loadResidents() {
    loadingResidents.value = true;
    errorResidents.value = null;

    try {
      recipients.value = await fetchManagerNotificationRecipients();
    } catch (error) {
      console.error('Ошибка загрузки списка резидентов:', error);
      errorResidents.value = error instanceof Error ? error.message : 'Неизвестная ошибка';
    } finally {
      loadingResidents.value = false;
    }
  }

  async function sendReply(formData: ReplyFormData) {
    const payload = new FormData();
    payload.append('parentId', String(formData.parentId));
    payload.append('subject', `Re: ${formData.subject || ''}`);
    payload.append('message', formData.message);

    const response = await fetch(getApiUrl('/api/replyNotificationsForm.php'), {
      method: 'POST',
      body: payload,
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.msg || 'Не удалось отправить ответ');
    }

    return result;
  }

  const resetAndLoadFirstPage = (
    residentId: string | null = null,
    search: string = '',
  ) => {
    nav.currentPage = 1;
    return loadNotifications(residentId, 1, search);
  };

  const showError = (msg: string) => {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: msg,
      life: 5000,
    });
  };

  const markAsRead = async (id: number): Promise<boolean> => {
    try {
      await markManagerNotificationRead(id);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка при пометке как прочитанное';
      console.error('Ошибка при пометке как прочитанное:', err);
      showError(message);
      return false;
    }
  };

  return {
    messages,
    loadingNotifications,
    errorNotifications,
    nav,
    recipients,
    loadingResidents,
    errorResidents,
    loadNotifications,
    loadResidents,
    sendReply,
    resetAndLoadFirstPage,
    markAsRead,
  };
}
